import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useUpdateInterviewMutation } from "@/hooks/api/interview.queries";
import {
  InterviewStatusValue,
  InterviewType,
} from "@/services/interview.service";
import { Star } from "lucide-react";
import React, { useEffect, useState } from "react";

interface InterviewOutcomeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  interview: InterviewType | null;
  internshipId: string;
  applicationId: string;
  /**
   * `completed` is the ONLY way the application reaches `interviewed` —
   * without it the pipeline stalls at `interview_scheduled`.
   * `no_show` records a miss without advancing the application.
   * `edit` re-saves feedback/rating on an already-closed round.
   */
  outcome: "completed" | "no_show" | "edit";
}

const outcomeCopy: Record<
  InterviewOutcomeDialogProps["outcome"],
  { title: string; description: string; submit: string; pending: string }
> = {
  completed: {
    title: "Mark Interview Completed",
    description:
      "This moves the application to Interviewed and unblocks the offer stage. The student is not emailed.",
    submit: "Mark Completed",
    pending: "Saving...",
  },
  no_show: {
    title: "Mark as No Show",
    description:
      "Records that the candidate did not attend. The application stays where it is.",
    submit: "Mark No Show",
    pending: "Saving...",
  },
  edit: {
    title: "Update Evaluation",
    description: "Revise the internal rating and feedback for this round.",
    submit: "Save Evaluation",
    pending: "Saving...",
  },
};

const InterviewOutcomeDialog: React.FC<InterviewOutcomeDialogProps> = ({
  isOpen,
  onClose,
  interview,
  internshipId,
  applicationId,
  outcome,
}) => {
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState("");

  const updateMutation = useUpdateInterviewMutation(internshipId, applicationId);
  const copy = outcomeCopy[outcome];

  useEffect(() => {
    if (isOpen && interview) {
      setRating(interview.rating ?? 0);
      setFeedback(interview.feedback ?? "");
    }
  }, [isOpen, interview]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!interview) return;

    updateMutation.mutate(
      {
        interviewId: interview.id,
        data: {
          // `edit` only touches the evaluation; it must not re-send a status.
          ...(outcome === "edit"
            ? {}
            : { status: outcome as InterviewStatusValue }),
          ...(rating > 0 ? { rating } : {}),
          ...(feedback.trim() ? { feedback: feedback.trim() } : {}),
        },
        successMessage:
          outcome === "completed"
            ? "Interview marked completed"
            : outcome === "no_show"
              ? "Interview marked as no show"
              : "Evaluation saved",
        successDescription:
          outcome === "completed"
            ? "The application has moved to Interviewed."
            : undefined,
      },
      { onSuccess: onClose }
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{copy.title}</DialogTitle>
            <DialogDescription>{copy.description}</DialogDescription>
          </DialogHeader>

          <div className="grid gap-6 py-4">
            <div className="space-y-2">
              <Label>Rating</Label>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    aria-label={`Rate ${star} out of 5`}
                    onClick={() => setRating(star === rating ? 0 : star)}
                    className="focus:outline-none"
                  >
                    <Star
                      className={`h-6 w-6 ${
                        star <= rating
                          ? "fill-yellow-400 text-yellow-400"
                          : "text-gray-300"
                      }`}
                    />
                  </button>
                ))}
                {rating > 0 && (
                  <span className="ml-2 text-sm font-semibold text-gray-600">
                    {rating}/5
                  </span>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="feedback">Feedback</Label>
              <Textarea
                id="feedback"
                placeholder="Enter candidate evaluation feedback..."
                className="min-h-[120px]"
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
              />
              <p className="text-xs text-gray-500">
                Rating and feedback stay inside the recruiter product — the
                student never sees them.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              type="button"
              onClick={onClose}
              disabled={updateMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending ? copy.pending : copy.submit}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default InterviewOutcomeDialog;
